import React, { useEffect, useState } from 'react';
import {
  Grid,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  signInDeafaultUser,
  getConfig,
  setClosedReason,
} from '../../services/firebase';
import {
  getAdminConfig,
  setStoreOpen,
  setAutoAccept,
  getCellCommercialConfig,
} from '../../actions/orderActions';
import { getPointOfSales } from '../../actions/adminActions';
import { Notification, newNotification } from '../../actions/uiActions';
import { connect } from 'react-redux';
import './Admin.css';
import { RootState } from '../../store/configureStore';
import { Cell, Order, PointOfSale, PointOfSaleAvailability } from '@m4a3/api-client';
import { getOpeningHoursForDay, isPointOfSaleClosedToday } from '../../utils/openingHourUtils';
import { convertUtcTimeStringForTimezone, isoStringToDateTimeObject } from '../../utils/date-utils';
import { CellCommercialConfig } from '@m4a3/api-client/dist/models/cell-commercial-config.interface';
import CellCommercialConfigEditor from './CellCommercialConfigEditor';
import { logToDatadog } from '../../services/datadog';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { ArrowPathIcon, ClockIcon } from '@heroicons/react/20/solid';

import { API } from 'aws-amplify';
import { orderfulApi } from '../../services/api-instances';

interface AvailabilityProps {
  cells: Cell[];
  cellCommercialConfig: CellCommercialConfig;
  isCellCommercialConfigLoading: boolean;
  cellCommercialConfigLoadingError: Error;
  pointOfSales: PointOfSale[];
  selectedCellId: number;
  notify: (notif: Notification) => void;
  getAdminConfig: () => void;
  adminSettings: any;
  setStoreOpen: (val: number) => void;
  setAutoAccept: (val: number) => void;
  getCellCommercialConfig: (cellId: number) => void;
  getPointOfSales: (cellId: number) => void;
  ordersToFilter: Order[];
}
const Availability = ({
  cells,
  cellCommercialConfig,
  selectedCellId,
  isCellCommercialConfigLoading,
  cellCommercialConfigLoadingError,
  pointOfSales,
  notify,
  getAdminConfig,
  adminSettings,
  setStoreOpen,
  setAutoAccept,
  getCellCommercialConfig,
  getPointOfSales,
  ordersToFilter,
}: AvailabilityProps) => {
  const [enableStoreOpenSwitch, setEnableStoreOpenSwitch] = useState(false);
  const [enableAutoAcceptSwitch, setEnableAutoAcceptSwitch] = useState(false);
  const [closedReason, setClosedReasonState] = useState('');
  const [allPointOfSaleAvailability, setAllPointOfSaleAvailability] = useState<{ [key: number]: PointOfSaleAvailability }>({});
  const [isLoadingAllPointOfSaleAvailability, setIsLoadingAllPointOfSaleAvailability] = useState<boolean>(false);
  const [updatingLargeOrders, setUpdatingLargeOrders] = useState<{[key: number]: boolean}>({});
  const [updatingAvailabilityMode, setUpdatingAvailabilityMode] = useState<{[key: number]: boolean}>({});
  const [selectedLandingZone, setSelectedLandingZone] = useState<string>('all');
  const [landingZoneMap, setLandingZoneMap] = useState<{[key: string]: string}>({});
  const [isLoadingLandingZoneNames, setIsLoadingLandingZoneNames] = useState<boolean>(false);
  const [showHiddenStores, setShowHiddenStores] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<'name' | 'status'>('name');
  const [isBulkUpdatingAvailability, setIsBulkUpdatingAvailability] = useState<boolean>(false);
  const [isBulkUpdatingLargeOrders, setIsBulkUpdatingLargeOrders] = useState<boolean>(false);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date>(new Date());

  useEffect(() => {
    getAdminConfig();
    getAndSetSoonestDeliveryTimeForPointOfSales();
    signInDeafaultUser()
      .then(() => {
        setUpConfig();
      })
      .catch(err => {
        console.log('COULD NOT SIGN IN FIREBASE: ', err);
      });
    
    // Set initial refresh time
    setLastRefreshTime(new Date());
    
    return () => {
    };
  }, []);

  // Extract unique landing zones from point of sales data and fetch names
  useEffect(() => {
    let isMounted = true;
    
    if (pointOfSales?.length && selectedCellId) {
      // Create a map of landing zone IDs to names (initially just using the ID as the name)
      const zoneMap: {[key: string]: string} = {};
      
      pointOfSales.forEach(pos => {
        if (pos.landingZoneId) {
          zoneMap[pos.landingZoneId] = pos.landingZoneId;
        }
      });
      
      if (isMounted) {
        setLandingZoneMap(zoneMap);
      }
      
      // Fetch landing zone names
      const fetchLandingZoneNames = async () => {
        if (!isMounted) return;
        
        setIsLoadingLandingZoneNames(true);
        try {
          const response = await API.get('orderful-api', `/cells/${selectedCellId}/landing-zones`, {});
          if (isMounted && response?.entities && response.entities.length > 0) {
            // Get the current state to ensure we have the latest
            setLandingZoneMap(currentMap => {
              const newZoneMap = { ...currentMap };
              
              // Add all landing zones from the API response
              response.entities.forEach(zone => {
                newZoneMap[zone.id] = zone.name || zone.id;
              });
              
              return newZoneMap;
            });
          }
        } catch (error) {
          if (isMounted) {
            console.error('Failed to fetch landing zone names:', error);
          }
        } finally {
          if (isMounted) {
            setIsLoadingLandingZoneNames(false);
          }
        }
      };
      
      fetchLandingZoneNames();
    }
    
    return () => {
      isMounted = false;
    };
  }, [pointOfSales, selectedCellId]);

  // Fetch landing zone names from API - used by refresh button
  const fetchLandingZoneNames = async () => {
    if (!selectedCellId) return;
    
    setIsLoadingLandingZoneNames(true);
    try {
      const response = await API.get('orderful-api', `/cells/${selectedCellId}/landing-zones`, {});
      if (response?.entities && response.entities.length > 0) {
        // Get the current state to ensure we have the latest
        setLandingZoneMap(currentMap => {
          const newZoneMap = { ...currentMap };
          
          // Add all landing zones from the API response
          response.entities.forEach(zone => {
            newZoneMap[zone.id] = zone.name || zone.id;
          });
          
          return newZoneMap;
        });
      }
    } catch (error) {
      console.error('Failed to fetch landing zone names:', error);
    } finally {
      setIsLoadingLandingZoneNames(false);
    }
  };

  useEffect(() => {
    if (adminSettings) {
      setEnableAutoAcceptSwitch(adminSettings.autoaccepton);
      setEnableStoreOpenSwitch(adminSettings.storeopen);
    }
  }, [adminSettings]);

  const setUpConfig = () => {
    getConfig(
      config => {
        const envIsDEV =
          process.env.REACT_APP_DELIVERY_URL.includes('dev') ||
          process.env.REACT_APP_DELIVERY_URL.includes('uat') ||
          process.env.NODE_ENV === 'development';
        if (envIsDEV) {
          setClosedReasonState(config.eircodeFormDisabledReason_DEV);
        } else {
          setClosedReasonState(config.eircodeFormDisabledReason);
        }
      },
      err => {
        console.log('err', err);
      }
    );
  };

  const getAndSetSoonestDeliveryTimeForPointOfSales = async () => {
    setIsLoadingAllPointOfSaleAvailability(true);
    try {
      const cellAvailability = await orderfulApi.getSoonestAvailabilityForCell(selectedCellId);
      setAllPointOfSaleAvailability(cellAvailability);
    } catch (error) {
      logToDatadog('get_pos_Availability_for_cell_failed', {selectedCellId}, 'error');
    }
    setIsLoadingAllPointOfSaleAvailability(false);
  }

  // Updates availablity API
  const handleChangeStoreOpen = async (e, isChecked: boolean) => {
    try {
      if (!isChecked) {
        setStoreOpen(0);
      } else {
        setStoreOpen(1);
      }
      // soft-launching this feature
      const updatedCellCommercialConfig: CellCommercialConfig = {
        ...cellCommercialConfig,
        isOpenForOperations: isChecked
      };
      await orderfulApi.updateCellCommercialConfig(updatedCellCommercialConfig);
      getCellCommercialConfig(selectedCellId);
    } catch (e) {
      notify({ variant: 'error', message: 'Could not update cell open' });
    }
  };

  // Updates firebase
  const handleChangeClosedReason = async evt => {
    try {
      const cellClosedReason = evt.target.value;
      await setClosedReason(cellClosedReason);
      setClosedReasonState(cellClosedReason);

      // soft-launching this feature
      const updatedCellCommercialConfig: CellCommercialConfig = {
        ...cellCommercialConfig,
        cellClosedReason
      };
      await orderfulApi.updateCellCommercialConfig(updatedCellCommercialConfig);
      getCellCommercialConfig(selectedCellId);
    } catch (err) {
      notify({ variant: 'error', message: 'Could not update closed reason' });
    }
  };

  // Updates availablity API
  const handleChangeAutoAccept = async (e, state) => {
    try {
      if (!state) {
        setAutoAccept(0);
      } else {
        setAutoAccept(1);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const updateCellCommercialConfig = async (config: CellCommercialConfig): Promise<void> => {
    try {
      await orderfulApi.updateCellCommercialConfig(config);
    } catch (error) {
      logToDatadog('update_cell_commercial_config_failed', {config, error}, 'error');
      notify({ variant: 'error', message: 'Could not get cell commercial config' });
    }
    getCellCommercialConfig(selectedCellId);
  };

  const getOpeningHoursForPointOfSale = (pointOfSale: PointOfSale): string => {
    const today = new Date();
    const openingHours = getOpeningHoursForDay(today, pointOfSale);
    const closedMessage = 'Closed';
  
    if (isPointOfSaleClosedToday(pointOfSale)) {
      return closedMessage;
    }
  
    // Format start time to HH:mm 
    const startTimeParts = openingHours?.startTime.split(':');
    const formattedStartTime = startTimeParts ? 
      `${startTimeParts[0].padStart(2, '0')}:${startTimeParts[1].padStart(2, '0')}` : 
      openingHours?.startTime;
  
    // Format end time to HH:mm 
    const endTimeParts = openingHours?.endTime.split(':');
    const formattedEndTime = endTimeParts ? 
      `${endTimeParts[0].padStart(2, '0')}:${endTimeParts[1].padStart(2, '0')}` : 
      openingHours?.endTime;
  
    return `${formattedStartTime} - ${formattedEndTime}`;
  }

  const getAvailabilityElementForPointOfSaleId = (pointOfSale: PointOfSale): JSX.Element => {
    if (isLoadingAllPointOfSaleAvailability) {
      return <LoadingSpinner />;
    }

    if (isPointOfSaleClosedToday(pointOfSale)) {
      return <span>N/A</span>;
    }

    const availabilityForPointOfSale = allPointOfSaleAvailability[pointOfSale.id];
    if (availabilityForPointOfSale?.endTimeUtc) {
      const soonestDelivery = isoStringToDateTimeObject(availabilityForPointOfSale.endTimeUtc, availabilityForPointOfSale.timezone)
        .toJSDate()
        .toISOString();

      return <span>{convertUtcTimeStringForTimezone(soonestDelivery, availabilityForPointOfSale.timezone)}</span>;
    }

    return <span>None found.</span>
  }

  const reloadAvailabilityData = () => {
    // Show notification that refresh is in progress
    notify({ variant: 'info', message: 'Refreshing store configuration data...' });
    
    // Refresh admin config and settings
    getAdminConfig();
    setUpConfig();
    
    // Refresh cell commercial config
    getCellCommercialConfig(selectedCellId);
    
    // Refresh point of sale data
    getPointOfSales(selectedCellId);
    
    // Refresh availability data
    getAndSetSoonestDeliveryTimeForPointOfSales();
    
    // Refresh landing zone names
    fetchLandingZoneNames();
    
    // Reset any updating states
    setUpdatingLargeOrders({});
    setUpdatingAvailabilityMode({});
    setIsBulkUpdatingAvailability(false);
    setIsBulkUpdatingLargeOrders(false);
    
    // Update last refresh time
    setLastRefreshTime(new Date());
    
    // Notify user that refresh is complete
    setTimeout(() => {
      notify({ variant: 'success', message: 'Store configuration data refreshed successfully' });
    }, 1000);
  }

  const sortPointOfSales = (a: PointOfSale, b: PointOfSale): number => {
    // Always prioritize display status if sorting by status
    if (sortBy === 'status') {
      if (a.displayStore && !b.displayStore) return -1;
      if (!a.displayStore && b.displayStore) return 1;
    }
    
    // Then sort by name
    return a.posName.localeCompare(b.posName);
  };

  const getLandingZoneName = (zoneId: string): string => {
    return landingZoneMap[zoneId] || zoneId;
  };

  const getUniqueLandingZones = (): {id: string, name: string}[] => {
    return Object.entries(landingZoneMap)
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const filteredPointOfSales = (): PointOfSale[] => {
    if (!pointOfSales) return [];
    
    let filtered = pointOfSales;
    
    // Filter by landing zone if not "all"
    if (selectedLandingZone !== 'all') {
      filtered = filtered.filter(pos => pos.landingZoneId === selectedLandingZone);
    }
    
    // Filter out hidden stores unless showHiddenStores is true
    if (!showHiddenStores) {
      filtered = filtered.filter(pos => pos.displayStore);
    }
    
    return filtered;
  };

  const handleLandingZoneChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedLandingZone(e.target.value);
  };

  const handleToggleLargeOrders = async (pointOfSale: PointOfSale) => {
    try {
      setUpdatingLargeOrders(prev => ({...prev, [pointOfSale.id]: true}));
      await API.put('orderful-api', 
        `/cells/${selectedCellId}/merchants/${pointOfSale.id}/enabledLargeOrders/${!pointOfSale.largeOrderingEnabled}/`,
        {}
      );
      // Update both admin config and point of sales data
      getAdminConfig();
      getPointOfSales(selectedCellId);
      
    } catch (error) {
      notify({ variant: 'error', message: 'Failed to update large orders setting' });
    } finally {
      setUpdatingLargeOrders(prev => ({...prev, [pointOfSale.id]: false}));
    }
  };

  const handleToggleAvailabilityMode = async (pointOfSale: PointOfSale) => {
    try {
      setUpdatingAvailabilityMode(prev => ({...prev, [pointOfSale.id]: true}));
      
      const newMode = pointOfSale.availabilityMode === 'NORMAL' ? 'BUSY' : 'NORMAL';
      await API.put('orderful-api', 
        `/cells/${selectedCellId}/merchants/${pointOfSale.id}/availabilityMode/${newMode}/`,
        {}
      );

      // Update both admin config and point of sales data
      getAdminConfig();
      getPointOfSales(selectedCellId);
      
    } catch (error) {
      notify({ variant: 'error', message: 'Failed to update availability mode' });
    } finally {
      setUpdatingAvailabilityMode(prev => ({...prev, [pointOfSale.id]: false}));
    }
  };

  const handleBulkUpdateAvailabilityMode = async (mode: 'NORMAL' | 'BUSY') => {
    const storesToUpdate = filteredPointOfSales();
    if (storesToUpdate.length === 0) return;
    
    try {
      setIsBulkUpdatingAvailability(true);
      
      // Create an array of promises for all updates
      const updatePromises = storesToUpdate.map(async (pos) => {
        // Only update if the current mode is different
        if (pos.availabilityMode !== mode) {
          return API.put('orderful-api', 
            `/cells/${selectedCellId}/merchants/${pos.id}/availabilityMode/${mode}/`,
            {}
          );
        }
        return Promise.resolve();
      });
      
      // Wait for all updates to complete
      await Promise.all(updatePromises);
      
      // Update both admin config and point of sales data
      getAdminConfig();
      getPointOfSales(selectedCellId);
      
      notify({ variant: 'success', message: `Set ${storesToUpdate.length} stores to ${mode} mode` });
      
    } catch (error) {
      notify({ variant: 'error', message: 'Failed to update availability mode for all stores' });
    } finally {
      setIsBulkUpdatingAvailability(false);
    }
  };

  const handleBulkUpdateLargeOrders = async (enabled: boolean) => {
    const storesToUpdate = filteredPointOfSales();
    if (storesToUpdate.length === 0) return;
    
    try {
      setIsBulkUpdatingLargeOrders(true);
      
      // Create an array of promises for all updates
      const updatePromises = storesToUpdate.map(async (pos) => {
        // Only update if the current state is different
        if (pos.largeOrderingEnabled !== enabled) {
          return API.put('orderful-api', 
            `/cells/${selectedCellId}/merchants/${pos.id}/enabledLargeOrders/${enabled}/`,
            {}
          );
        }
        return Promise.resolve();
      });
      
      // Wait for all updates to complete
      await Promise.all(updatePromises);
      
      // Update both admin config and point of sales data
      getAdminConfig();
      getPointOfSales(selectedCellId);
      
      notify({ variant: 'success', message: `Set Large Orders ${enabled ? 'ON' : 'OFF'} for ${storesToUpdate.length} stores` });
      
    } catch (error) {
      notify({ variant: 'error', message: 'Failed to update large orders setting for all stores' });
    } finally {
      setIsBulkUpdatingLargeOrders(false);
    }
  };

  const handleShowHiddenStoresChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowHiddenStores(e.target.checked);
  };

  const getLogoUrl = (pointOfSale: PointOfSale): string => {
    // Use the logo property from the PointOfSale object
    return pointOfSale.logo || `https://ui-avatars.com/api/?name=${encodeURIComponent(pointOfSale.posName)}&background=random&color=fff&size=128`;
  };

  return (
    <div className="w-full h-[92%] mx-auto overflow-y-scroll p-5">
      <div className="py-1">
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <div className="flex items-center">
              <h3 className="inline mr-2 font-sans font-medium">
                Cell Open
              </h3>
              <FormControlLabel
                label={''}
                control={
                  <Switch
                    checked={enableStoreOpenSwitch}
                    onChange={handleChangeStoreOpen}
                    name="checkedB"
                    color="primary"
                  />
                }
              />
            </div>
          </Grid>

          <Grid item>
            <div className="flex items-center">
              <button
                type="button"
                onClick={reloadAvailabilityData}
                className="mt-1 inline-flex items-center gap-x-1.5 rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 transition-colors"
              >
                Refresh Store Config
                <ArrowPathIcon className="-mr-0.5 h-5 w-5 ml-1" aria-hidden="true" />
              </button>
              <div className="ml-2 text-xs text-gray-500 flex items-center">
                <ClockIcon className="h-3.5 w-3.5 mr-1 text-gray-400" />
                <span>Last updated: {lastRefreshTime.toLocaleTimeString()}</span>
              </div>
            </div>
          </Grid>
        </Grid>
      </div>
      <Divider className="my-4" />
      <div className='py-4'>
        <span className='font-semibold text-lg'>Parameters</span>
        <CellCommercialConfigEditor
          cellCommercialConfig={cellCommercialConfig}
          isLoading={isCellCommercialConfigLoading}
          cellCommercialConfigLoadingError={cellCommercialConfigLoadingError}
          onChangeDeliveryFlightTime={async (newflightTime: number) => {
            const updatedConfig: CellCommercialConfig = {
              ...cellCommercialConfig,
              droneFlightTimeInMinutes: newflightTime
            }
            await updateCellCommercialConfig(updatedConfig);
          }}
          onChangeOpsBufferTime={async (newOpsBufferTime: number) => {
            const updatedConfig: CellCommercialConfig = {
              ...cellCommercialConfig,
              opsBufferTimeInMinutes: newOpsBufferTime
            }
            await updateCellCommercialConfig(updatedConfig);
          }} />
      </div>
      <Divider className="my-4" />
      <div className="mt-5 mb-7">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6">
          <h3 className="font-semibold text-lg">Point of Sales</h3>
          <div className="flex flex-wrap items-center gap-4 mt-3 md:mt-0">
            <div className="flex items-center mr-2">
              <div className="relative w-full sm:w-auto">
                <select
                  id="landingZoneFilter"
                  className="w-full sm:w-64 p-2 pr-8 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white"
                  value={selectedLandingZone}
                  onChange={handleLandingZoneChange}
                >
                  <option value="all">All Landing Zones</option>
                  {(() => {
                    const zones = getUniqueLandingZones();
                    return zones.length > 0 ? (
                      zones.map(zone => (
                        <option key={zone.id} value={zone.id}>
                          {zone.name}
                        </option>
                      ))
                    ) : (
                      <option disabled>Loading landing zones...</option>
                    );
                  })()}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              {isLoadingLandingZoneNames && (
                <span className="text-xs text-gray-500 flex items-center ml-2">
                  <LoadingSpinner size={12} /> Loading names...
                </span>
              )}
            </div>
            <div className="flex items-center">
              <input
                id="showHiddenStores"
                type="checkbox"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                checked={showHiddenStores}
                onChange={handleShowHiddenStoresChange}
              />
              <label htmlFor="showHiddenStores" className="ml-2 text-sm font-medium text-gray-700">
                Show hidden stores
              </label>
            </div>
          </div>
        </div>
        
        <div className="mb-4 flex flex-wrap gap-4">
          <div className="flex items-center">
            <span className="text-sm font-medium text-gray-700 mr-2">Availability:</span>
            <div className="flex gap-2">
              <button
                onClick={() => handleBulkUpdateAvailabilityMode('NORMAL')}
                disabled={isBulkUpdatingAvailability}
                className="px-3 py-1.5 text-xs font-medium rounded-md bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20 hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isBulkUpdatingAvailability ? <LoadingSpinner size={12} /> : 'Set All NORMAL'}
              </button>
              <button
                onClick={() => handleBulkUpdateAvailabilityMode('BUSY')}
                disabled={isBulkUpdatingAvailability}
                className="px-3 py-1.5 text-xs font-medium rounded-md bg-yellow-50 text-yellow-700 ring-1 ring-inset ring-yellow-600/20 hover:bg-yellow-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isBulkUpdatingAvailability ? <LoadingSpinner size={12} /> : 'Set All BUSY'}
              </button>
            </div>
          </div>
          
          <div className="flex items-center">
            <span className="text-sm font-medium text-gray-700 mr-2">Large Orders:</span>
            <div className="flex gap-2">
              <button
                onClick={() => handleBulkUpdateLargeOrders(true)}
                disabled={isBulkUpdatingLargeOrders}
                className="px-3 py-1.5 text-xs font-medium rounded-md bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20 hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isBulkUpdatingLargeOrders ? <LoadingSpinner size={12} /> : 'Set All ON'}
              </button>
              <button
                onClick={() => handleBulkUpdateLargeOrders(false)}
                disabled={isBulkUpdatingLargeOrders}
                className="px-3 py-1.5 text-xs font-medium rounded-md bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20 hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isBulkUpdatingLargeOrders ? <LoadingSpinner size={12} /> : 'Set All OFF'}
              </button>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <div className="min-w-full rounded-lg border border-gray-200 overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-3 bg-gray-50 border-b border-gray-200">
              <div className="font-semibold text-sm text-gray-700 p-3">Point of sale</div>
              <div className="font-semibold text-sm text-gray-700 p-3 sm:col-span-2">Status</div>
            </div>
            
            {/* Table Body */}
            <div className="divide-y divide-gray-200">
              {filteredPointOfSales().slice().sort(sortPointOfSales).map((pointOfSale: PointOfSale, index) => {
                return (
                  <div 
                    key={pointOfSale.id} 
                    className={`grid grid-cols-1 gap-x-4 sm:grid-cols-3 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}
                  >
                    <div className="p-3">
                      <div className="flex items-center">
                        <img 
                          src={getLogoUrl(pointOfSale)} 
                          alt={`${pointOfSale.posName} logo`} 
                          className="w-10 h-10 rounded-full object-cover mr-3 border border-gray-200"
                          onError={(e) => {
                            // Fallback if image fails to load
                            (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(pointOfSale.posName)}&background=random&color=fff&size=128`;
                          }}
                        />
                        <div>
                          <div className="font-medium">{pointOfSale.posName}</div>
                          {pointOfSale.landingZoneId && (
                            <div className="text-xs text-gray-500 mt-1">
                              {getLandingZoneName(pointOfSale.landingZoneId)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="p-3 sm:col-span-2">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={`inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium ${pointOfSale.displayStore ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-red-50 text-red-700 ring-red-600/20'} ring-1 ring-inset`}>
                          <svg className={`h-1.5 w-1.5`} fill="currentColor" viewBox="0 0 6 6" aria-hidden="true">
                            <circle cx={3} cy={3} r={3} />
                          </svg>
                          {pointOfSale.displayStore ? 'Showing' : 'Hidden'}
                        </span>
                        <button 
                          onClick={() => handleToggleAvailabilityMode(pointOfSale)}
                          className={`inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium ${pointOfSale.availabilityMode === 'NORMAL' ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-yellow-50 text-yellow-700 ring-yellow-600/20'} ring-1 ring-inset cursor-pointer hover:bg-opacity-80 transition-colors`}
                        >
                          <svg className={`h-1.5 w-1.5`} fill="currentColor" viewBox="0 0 6 6" aria-hidden="true">
                            <circle cx={3} cy={3} r={3} />
                          </svg>
                          {updatingAvailabilityMode[pointOfSale.id] ? (
                            <LoadingSpinner size={12} />
                          ) : (
                            pointOfSale.availabilityMode
                          )}
                        </button>
                        <button 
                          onClick={() => handleToggleLargeOrders(pointOfSale)}
                          className={`inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium ${pointOfSale.largeOrderingEnabled ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-red-50 text-red-700 ring-red-600/20'} ring-1 ring-inset cursor-pointer hover:bg-opacity-80 transition-colors`}
                        >
                          <svg className={`h-1.5 w-1.5`} fill="currentColor" viewBox="0 0 6 6" aria-hidden="true">
                            <circle cx={3} cy={3} r={3} />
                          </svg>
                          {updatingLargeOrders[pointOfSale.id] ? (
                            <LoadingSpinner size={12} />
                          ) : (
                            'Large'
                          )}
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-x-4 text-xs text-gray-600">
                        <div className="flex items-center">
                          <span className="font-medium mr-1">Hours:</span> {getOpeningHoursForPointOfSale(pointOfSale)}
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium mr-1">Lead time:</span> {pointOfSale.leadTime} minutes
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium mr-1">Next available:</span> {getAvailabilityElementForPointOfSaleId(pointOfSale)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {filteredPointOfSales().length === 0 && (
                <div className="p-4 text-center text-gray-500">
                  No point of sales found for the selected landing zone.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  cells: state.orders.cells,
  cellCommercialConfig: state.orders.cellCommercialConfig,
  isCellCommercialConfigLoading: state.orders.isCellCommercialConfigLoading,
  cellCommercialConfigLoadingError: state.orders.cellCommercialConfigLoadingError,
  selectedCellId: state.orders.selectedCellId,
  pointOfSales: state.admin.pointOfSales,
  adminSettings: state.orders.adminSettings,
  ordersToFilter: state.orders.ordersToFilter,
});

const mapDispatchToProps = (dispatch) => ({
  notify: (notification: Notification) => dispatch(newNotification(notification)),
  getAdminConfig: () => dispatch(getAdminConfig()),
  setAutoAccept: (val: number) => dispatch(setAutoAccept(val)),
  setStoreOpen: (val: number) => dispatch(setStoreOpen(val)),
  getCellCommercialConfig: (cellId: number) => dispatch(getCellCommercialConfig(cellId)),
  getPointOfSales: (cellId: number) => dispatch(getPointOfSales(cellId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Availability);
